import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Check, ChevronDown, Globe } from 'lucide-react'
import { SUPPORTED_LOCALES } from '../../i18n'
import { setApiLocale } from '../../lib/api/client'
import type { Locale } from '../../lib/api/types'
import { cn } from '../../lib/cn'

// Endonyms: a language is always listed the way its own speakers write it, never translated.
const LANGUAGE_NAME: Record<Locale, string> = {
  it: 'Italiano',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  hr: 'Hrvatski',
  sl: 'Slovenščina',
  zh: '中文',
}

const MENU_ANIM = `
  @keyframes gsmLangIn { from { opacity: 0; transform: translateY(-4px) scale(0.97) } to { opacity: 1; transform: none } }
  .gsm-lang-menu { animation: gsmLangIn 140ms cubic-bezier(0.16, 1, 0.3, 1) }
  @media (prefers-reduced-motion: reduce) { .gsm-lang-menu { animation: none } }
`

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { locale } = useParams()
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  const current = ((locale ?? i18n.language) as Locale) ?? 'it'

  // Close on outside click or Escape — the menu overlays page content.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function change(next: Locale) {
    setOpen(false)
    if (next === current) return
    i18n.changeLanguage(next)
    setApiLocale(next)
    try {
      window.localStorage.setItem('gsm_locale', next)
    } catch {
      /* private mode — the URL still carries the choice */
    }
    // swap the locale segment in the URL, keep the rest of the path
    const parts = window.location.pathname.split('/').filter(Boolean)
    if (parts[0] && SUPPORTED_LOCALES.includes(parts[0] as Locale)) parts[0] = next
    else parts.unshift(next)
    navigate('/' + parts.join('/'))
  }

  return (
    <div ref={boxRef} className="relative">
      <style>{MENU_ANIM}</style>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={LANGUAGE_NAME[current]}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-ink-700 ring-1 transition',
          open ? 'bg-white ring-ink-200' : 'bg-ink-100/70 ring-transparent hover:bg-ink-100',
        )}
      >
        <Globe className="h-4 w-4 text-ink-400" />
        <span className="uppercase tracking-wide">{current}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-ink-400 transition-transform duration-200', open && '-rotate-180')} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="gsm-lang-menu absolute right-0 z-50 mt-2 w-[12.5rem] origin-top-right rounded-2xl bg-white p-1.5 shadow-[0_12px_32px_-8px_rgba(16,24,40,0.18)] ring-1 ring-ink-900/5"
        >
          {SUPPORTED_LOCALES.map((l) => (
            <li key={l}>
              <button
                type="button"
                role="option"
                aria-selected={l === current}
                onClick={() => change(l)}
                className={cn(
                  'group flex w-full items-center gap-2.5 rounded-xl px-2 py-[7px] text-left text-[13.5px] transition',
                  l === current ? 'bg-brand-50 text-ink-900' : 'text-ink-700 hover:bg-ink-50',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-7 flex-none items-center justify-center rounded-md text-[10px] font-bold uppercase tracking-wide transition',
                    l === current
                      ? 'bg-brand-600 text-white'
                      : 'bg-ink-100 text-ink-500 group-hover:bg-ink-200/70 group-hover:text-ink-600',
                  )}
                >
                  {l}
                </span>
                <span className={cn('flex-1 truncate', l === current && 'font-semibold')}>{LANGUAGE_NAME[l]}</span>
                {l === current && <Check className="h-4 w-4 flex-none text-brand-600" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
